import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Calendar, User, FileText, TrendingUp, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getCalls, getUsers } from '@/api/api';
import { format, subDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useActionHistory } from '@/context/ActionHistoryContext';

const GeneralCalls = ({ user }) => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRep, setSelectedRep] = useState('all');
  const [dateFilter, setDateFilter] = useState('30');
  const [searchQuery, setSearchQuery] = useState(''); // ARAMA FİLTRESİ
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('call_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const { pushAction } = useActionHistory();
  const itemsPerPage = 20;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [callsData, usersData] = await Promise.all([
        getCalls(),
        getUsers()
      ]);
      setCalls(callsData);
      setUsers(usersData);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter calls by date and representative
  const getFilteredCalls = () => {
    let filtered = [...calls];

    // Date filter
    if (dateFilter !== 'all') {
      const days = parseInt(dateFilter);
      const cutoffDate = subDays(new Date(), days);
      filtered = filtered.filter(call => new Date(call.call_date) >= cutoffDate);
    }

    // Representative filter
    if (selectedRep !== 'all') {
      filtered = filtered.filter(call => call.representative === selectedRep);
    }

    // Arama filtresi
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(call => 
        call.customer_name?.toLowerCase().includes(query) ||
        call.representative?.toLowerCase().includes(query) ||
        call.notes?.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  useEffect(() => {
    setCurrentPage(1); // Arama/filtre değişince 1. sayfaya dön
  }, [searchQuery, selectedRep, dateFilter]);

  const filteredCalls = getFilteredCalls();

  // Generate statistics
  const getRepStats = () => {
    const stats = {};
    
    users.forEach(u => {
      stats[u.username] = {
        name: u.full_name,
        total: 0
      };
    });

    filteredCalls.forEach(call => {
      if (stats[call.representative]) {
        stats[call.representative].total += 1;
      }
    });

    return Object.entries(stats)
      .map(([username, data]) => ({
        username,
        name: data.name,
        total: data.total
      }))
      .sort((a, b) => b.total - a.total);
  };

  const repStats = getRepStats();

  const sortedCalls = [...filteredCalls].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortField === 'call_date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const paginatedCalls = sortedCalls.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedCalls.length / itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Phone className="w-8 h-8 mr-3 text-blue-600" />
          Genel Aramalar
        </h1>
        <p className="text-gray-600 mt-1">Tüm arama kayıtlarını ve istatistiklerini görüntüleyin</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6 space-y-4">
        {/* Arama Kutusu */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Arama</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Müşteri, temsilci veya not ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Temsilci</label>
            <Select value={selectedRep} onValueChange={(newValue) => {
              const oldValue = selectedRep;
              setSelectedRep(newValue);
              pushAction({
                undo: () => setSelectedRep(oldValue)
              });
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Temsilciler</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.username} value={u.username}>
                    {u.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tarih Aralığı</label>
            <Select value={dateFilter} onValueChange={(newValue) => {
              const oldValue = dateFilter;
              setDateFilter(newValue);
              pushAction({
                undo: () => setDateFilter(oldValue)
              });
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Son 7 Gün</SelectItem>
                <SelectItem value="30">Son 30 Gün</SelectItem>
                <SelectItem value="90">Son 90 Gün</SelectItem>
                <SelectItem value="all">Tüm Zamanlar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Toplam Arama</div>
          <div className="text-2xl font-bold text-blue-600">{filteredCalls.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Aktif Temsilci</div>
          <div className="text-2xl font-bold text-green-600">
            {repStats.filter(r => r.total > 0).length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Günlük Ortalama</div>
          <div className="text-2xl font-bold text-orange-600">
            {dateFilter !== 'all' 
              ? Math.round(filteredCalls.length / parseInt(dateFilter))
              : '-'}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">En Çok Arayan</div>
          <div className="text-sm font-bold text-purple-600">
            {repStats[0]?.name || '-'}
          </div>
          <div className="text-xs text-gray-500">
            {repStats[0]?.total || 0} arama
          </div>
        </div>
      </div>

      {/* Representative Performance Table */}
      <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
        <div className="px-6 py-4 progo-gradient">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <TrendingUp className="w-5 h-5 mr-2" />
            Temsilci Performansı
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Temsilci
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Toplam Arama
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {repStats.map((rep) => (
                <tr key={rep.username} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{rep.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{rep.total}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GeneralCalls;
